/**
 * The level ladder: "given a compelling offer, what levels would you actually
 * accept?" asked as three rungs that name the applicant's REAL neighbouring
 * levels rather than an abstract up/sideways/down scale.
 *
 * All of the naming logic is here rather than in the component so it can be
 * asserted without a DOM — the copy is the feature, and a rung that names the
 * wrong level is a content bug no render test would catch.
 *
 * This module also owns the level list itself, because the chips and the
 * rungs have to be the same ladder. `steps.ts` imports `LEVEL_OPTIONS` from
 * here; if the list lived there instead the import would run the other way
 * and close a cycle.
 *
 * And it owns the ladder's widget descriptor, at the bottom, beside the rules
 * the descriptor is built from. The registry in `widgets.ts` describes only
 * the widgets whose whole definition is "a string" or "a pick from a list";
 * anything with rules of its own keeps them and its descriptor together, the
 * way `job-timeline.ts` and `job-priorities.ts` do.
 */
import { asList, asText, optionSchema } from './answer-shape.js'
import type { AuditionAnswer, AuditionField } from './steps.js'
import type { WidgetDescriptor } from './widgets.js'

interface LevelDef {
  /** The chip label, carrying the L-ladder hint for people who think in levels. */
  option: string
  /**
   * What this level is called on the ladder, without the L-ladder hint —
   * "Towards Senior (L5 / SDE III) or Staff (L6)" reads like a form, not a
   * sentence. Absent means the level has no position on a ladder at all:
   * "Engineering Manager" and "Not sure" are real answers with nothing
   * above or below them, so they get no rung.
   */
  rung?: string
}

/**
 * Every answer to "what is your current level?", in ladder order. Order is
 * the meaning here — the entries above a level are the promotions and the
 * entries below are the down-levels — so this array is deliberately ordered
 * and the off-ladder answers are marked by the ABSENCE of `rung`, never by
 * their position in it.
 */
const LEVELS: readonly LevelDef[] = [
  { option: 'Intern / Student', rung: 'Intern / Student' },
  { option: 'Junior (L3 / SDE I)', rung: 'Junior' },
  { option: 'Mid-level (L4 / SDE II)', rung: 'Mid-level' },
  { option: 'Senior (L5 / SDE III)', rung: 'Senior' },
  { option: 'Staff (L6)', rung: 'Staff' },
  {
    option: 'Senior Staff / Principal (L7+)',
    rung: 'Senior Staff / Principal',
  },
  { option: 'Engineering Manager' },
  { option: 'Not sure' },
]

/**
 * Shared by the current-level and target-level chip groups so the two can
 * never drift. Title-based rather than big-tech numerics, with the L-ladder
 * in parentheses for candidates who think in levels.
 */
export const LEVEL_OPTIONS: readonly string[] = LEVELS.map((l) => l.option)

/** The rungs, bottom to top. Levels with no ladder position are not here. */
export const LADDER_RUNG_NAMES: readonly string[] = LEVELS.flatMap((l) =>
  l.rung ? [l.rung] : [],
)

/** The three directions of move, top rung first — the order they render in. */
export const MOVE_IDS = ['up', 'lateral', 'down'] as const

export type MoveId = (typeof MOVE_IDS)[number]

/**
 * How a stored move reads on its own — the chip label, and what the admin
 * review surface prints. Deliberately the full sentence rather than the id,
 * because "up" alone is not an answer anybody can review.
 */
const MOVE_LABELS: Record<MoveId, string> = {
  up: 'A step up',
  lateral: 'A lateral move at the same level',
  down: 'A step down for the right opportunity',
}

/** The same three, lowercased to sit mid-sentence in the readback. */
const MOVE_PHRASES: Record<MoveId, string> = {
  up: 'a step up',
  lateral: 'a lateral move at the same level',
  down: 'a step down for the right opportunity',
}

/** Sub-labels for an applicant whose level has no rung, or who has not said. */
const GENERIC_SUBS: Record<MoveId, string> = {
  up: 'A level above the one you hold today',
  lateral: 'The same level, somewhere that suits you better',
  down: 'A level below the one you hold today',
}

/**
 * How many neighbouring levels a rung names. Two is enough to make the
 * direction concrete; naming every level above turns the rung into a list.
 */
const NEIGHBOURS_NAMED = 2

export interface LadderRung {
  id: MoveId
  /** The rung's own label — the answer, in the applicant's words. */
  label: string
  /** Names their real neighbouring levels, or coaches generically. */
  sub: string
  /**
   * True when this direction has nowhere to go from their level. The rung is
   * rendered unpickable rather than accepting an answer the ladder cannot
   * mean. Named for what it means rather than for the HTML attribute, which
   * it deliberately is not: a closed rung stays focusable so its explanation
   * can be read.
   */
  closed: boolean
  /**
   * True on the rung the applicant is standing on, for the "you are here"
   * mark. False for every rung when their level has no position on the
   * ladder, because there is nothing to mark.
   */
  isHere: boolean
}

/** Where a chip answer sits on the ladder, or -1 for an answer with no rung. */
function rungIndexOf(currentLevel: string | undefined): number {
  if (!currentLevel) return -1
  const def = LEVELS.find((l) => l.option === currentLevel)
  return def?.rung ? LADDER_RUNG_NAMES.indexOf(def.rung) : -1
}

/**
 * The three rungs, named for whoever is standing on the ladder.
 *
 * A level at either end closes the rung it cannot reach. The alternative —
 * leaving it pickable under the words "there is nothing above your level" —
 * both accepts a meaningless answer and says the discouraging thing at the
 * most ambitious moment of the application.
 */
export function ladderRungs(currentLevel: string | undefined): LadderRung[] {
  const at = rungIndexOf(currentLevel)
  const onLadder = at !== -1
  const here = onLadder ? LADDER_RUNG_NAMES[at] : null

  const above = onLadder
    ? LADDER_RUNG_NAMES.slice(at + 1, at + 1 + NEIGHBOURS_NAMED)
    : []
  // Nearest first, so "Back to Junior or Intern / Student" walks down the
  // ladder the way the eye does.
  const below = onLadder
    ? LADDER_RUNG_NAMES.slice(Math.max(0, at - NEIGHBOURS_NAMED), at)
        .slice()
        .reverse()
    : []

  const subFor = (id: MoveId): string => {
    if (!onLadder) return GENERIC_SUBS[id]
    if (id === 'lateral')
      return `Stay at ${here}, somewhere that suits you better`
    if (id === 'up')
      return above.length
        ? `Towards ${above.join(' or ')}`
        : `${here} is the top of this ladder.`
    return below.length
      ? `Back to ${below.join(' or ')}`
      : `${here} is the first rung on this ladder.`
  }

  return MOVE_IDS.map((id) => ({
    id,
    label: MOVE_LABELS[id],
    sub: subFor(id),
    closed:
      onLadder &&
      ((id === 'up' && above.length === 0) ||
        (id === 'down' && below.length === 0)),
    isHere: id === 'lateral' && onLadder,
  }))
}

/**
 * The move a stored value means, or null when it means nothing we know.
 *
 * Before the ladder this question stored its option LABELS rather than ids,
 * so a draft saved then still holds "A step up". Those are the same answer
 * in an older vocabulary, and reading them back as the move they always were
 * is what lets a returning applicant see their own answer selected.
 */
export function moveId(stored: string): MoveId | null {
  if ((MOVE_IDS as readonly string[]).includes(stored)) return stored as MoveId
  const fromLabel = MOVE_IDS.find((id) => MOVE_LABELS[id] === stored)
  return fromLabel ?? null
}

/**
 * A stored move as display text.
 *
 * Unrecognised values pass straight through rather than rendering blank: a
 * move we later retire must still show the answer somebody gave us.
 */
export function moveLabel(stored: string): string {
  const id = moveId(stored)
  return id ? MOVE_LABELS[id] : stored
}

/**
 * The moves worth keeping for somebody standing at `currentLevel`: read in
 * today's vocabulary, in ladder order, with anything the ladder cannot mean
 * dropped.
 *
 * The dropping is the important half. `currentLevel` is answered on the same
 * step as the ladder, so an applicant can pick "a step up", then raise their
 * level to the top rung — and the pick they made would otherwise stay
 * selected on a rung that is now closed, uncloseable because the button is
 * disabled, contradicted by its own sub-label, and still submitted. The rung
 * going visibly grey with "X is the top of this ladder" underneath is the
 * explanation for why the pick went away.
 */
export function settleMoves(
  currentLevel: string | undefined,
  stored: readonly string[],
): MoveId[] {
  const open = new Set(
    ladderRungs(currentLevel)
      .filter((r) => !r.closed)
      .map((r) => r.id),
  )
  const said = new Set(stored.map(moveId).filter((id) => id !== null))
  return MOVE_IDS.filter((id) => said.has(id) && open.has(id))
}

/**
 * The picked moves as a sentence. A widget without a plain-English readback
 * is a puzzle: the rungs say what each option means, this says what the
 * applicant has actually claimed.
 */
export function movesReadback(moves: readonly string[]): string {
  // Read in ladder order however they were stored, so the sentence does not
  // change shape depending on which rung they tapped first.
  const said = MOVE_IDS.filter((id) => moves.includes(id)).map(
    (id) => MOVE_PHRASES[id],
  )
  if (said.length === 0) return 'Pick as many as apply.'
  const last = said[said.length - 1]
  const text =
    said.length === 1 ? last : `${said.slice(0, -1).join(', ')} or ${last}`
  return `You would accept ${text}.`
}

/**
 * The level a ladder field takes its rungs from, as answered on this step.
 *
 * One answer to that lookup for both halves of the widget: settling asks it
 * to decide which rungs are open, and the component asks it to name them.
 * Undefined means "not said yet", which the ladder draws generically.
 */
export function rungsLevel(
  field: AuditionField,
  answers: Record<string, AuditionAnswer | undefined>,
): string | undefined {
  return field.rungsFrom ? asText(answers[field.rungsFrom]) : undefined
}

/**
 * What the ladder stores, and how it reads back.
 *
 * Option-backed like a chip group and always multi-pick, so the field never
 * has to declare `multi` — and every reader that used to test `field.multi`
 * would have quietly treated this as a string.
 */
export const ladderWidget: WidgetDescriptor = {
  empty: () => [],
  is: (value) => typeof value === 'string' || Array.isArray(value),
  // A resumed draft may hold moves in the pre-ladder vocabulary, and changing
  // the level answer can close a rung the applicant had already picked. Left
  // alone, the widget shows one thing and Next submits another.
  settle: (value, ctx) =>
    settleMoves(rungsLevel(ctx.field, ctx.answers), asList(value)),
  storedSchema: (field) => optionSchema(field, true),
  // Settling already drops a rung that has closed, and the rung going grey
  // with its reason underneath is what says so.
  ready: () => true,
  // The ladder stores move ids, and "up" is not something to print at a
  // reviewer. An id we later retire passes straight through rather than
  // rendering blank.
  summary: (value) => asList(value).map(moveLabel),
  list: () => true,
  prose: false,
}
